import { action } from "typesafe-actions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";

import { uploadMultipleFile } from "../../components/FileUpload/utils";
import {
  IMasterQuickReplyTemplate,
  IMasterQuickReplyTemplateData,
  IMasterQuickReplyTemplatePayload,
  TemplateForType,
} from "./masterQuickReplyTemplate.types";

// Action Types
export const FETCH_MASTER_QUICK_REPLY_TEMPLATE_LIST_PROGRESS =
  "FETCH_MASTER_QUICK_REPLY_TEMPLATE_LIST_PROGRESS";
export const FETCH_MASTER_QUICK_REPLY_TEMPLATE_LIST_SUCCESS =
  "FETCH_MASTER_QUICK_REPLY_TEMPLATE_LIST_SUCCESS";
export const FETCH_MASTER_QUICK_REPLY_TEMPLATE_LIST_FAILED =
  "FETCH_MASTER_QUICK_REPLY_TEMPLATE_LIST_FAILED";

// Action Creators
export const fetchMasterQuickReplyTemplateListProgress = () =>
  action(FETCH_MASTER_QUICK_REPLY_TEMPLATE_LIST_PROGRESS);
export const fetchMasterQuickReplyTemplateListSuccess = (
  data: IMasterQuickReplyTemplate[],
  totalRecords: number
) => action(FETCH_MASTER_QUICK_REPLY_TEMPLATE_LIST_SUCCESS, { data, totalRecords });
export const fetchMasterQuickReplyTemplateListFailed = (errorMessage: string) =>
  action(FETCH_MASTER_QUICK_REPLY_TEMPLATE_LIST_FAILED);

// Async Actions - Matching fetchMarketingQuickReplyTemplatesListAsync pattern
export const fetchMasterQuickReplyTemplateListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: { fromDate: string; toDate: string },
    searchValue: any[], // IDatatableV2AdvancedSearchFilter
    company_outlet_uuid?: string,
    searchByName?: string,
    searchByTemplateId?: string,
    template_for?: TemplateForType,
    onCallBack?: (data: IMasterQuickReplyTemplate[]) => void,
    user_uuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchMasterQuickReplyTemplateListProgress());
        dispatch(saveLoaderProgress());

        let url = "";

        if (status !== "-1") {
          url += `&status=${status}`;
        }

        // if (template_for) {
        //   url += `&template_for=${template_for}`;
        // }
        if (company_outlet_uuid) {
          url += `&company_outlet_uuid=${company_outlet_uuid}`;
        }

        if (user_uuid) {
          url += `&user_uuid=${user_uuid}`;
        }

        if (searchByName) {
          url += `&columns=${JSON.stringify(["name"])}&value=${searchByName}`;
        }

        if (searchValue.length > 0 || searchByTemplateId) {
          const advanceFilter = [...searchValue];

          if (searchByTemplateId) {
            advanceFilter.push({
              column: ["template_id"],
              value: searchByTemplateId,
              logicalOperator: "AND",
              operator: "CONTAINS",
            });
          }

          url += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
        }

        if (date) {
          url += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
        }

        const finalUrl =
          `/template/get-master-quick-reply-template?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}` +
          url;

        const res = await api.get(finalUrl);

        const data: IMasterQuickReplyTemplate[] = res.data.data;
        const totalRecords = res.data.totalRecords || 0;

        dispatch(fetchMasterQuickReplyTemplateListSuccess(data, totalRecords));

        // Execute callback if provided
        if (onCallBack) {
          onCallBack(data);
        }
      } catch (err: any) {
        dispatch(fetchMasterQuickReplyTemplateListFailed(err?.response?.data?.message));
        dispatch(
          showMessage({
            type: "error",
            message: err?.response?.data?.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const FETCH_MASTER_QUICK_REPLY_TEMPLATE_PROGRESS =
  "FETCH_MASTER_QUICK_REPLY_TEMPLATE_PROGRESS";
export const FETCH_MASTER_QUICK_REPLY_TEMPLATE_SUCCESS =
  "FETCH_MASTER_QUICK_REPLY_TEMPLATE_SUCCESS";
export const FETCH_MASTER_QUICK_REPLY_TEMPLATE_FAILED =
  "FETCH_MASTER_QUICK_REPLY_TEMPLATE_FAILED";

export const fetchMasterQuickReplyTemplateProgress = () =>
  action(FETCH_MASTER_QUICK_REPLY_TEMPLATE_PROGRESS);
export const fetchMasterQuickReplyTemplateSuccess = (data: IMasterQuickReplyTemplateData) =>
  action(FETCH_MASTER_QUICK_REPLY_TEMPLATE_SUCCESS, { data });
export const fetchMasterQuickReplyTemplateFailed = (errorMessage: string) =>
  action(FETCH_MASTER_QUICK_REPLY_TEMPLATE_FAILED, { errorMessage });

export const fetchMasterQuickReplyTemplateAsync =
  (
    templateName?: string,
    companyOutletUuid?: string,
    templateUuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchMasterQuickReplyTemplateProgress());

        let url = "";
        if (templateName) {
          url += `&name=${templateName}`;
        }

        if (templateUuid) {
          url += `&quick_reply_uuid=${templateUuid}`;
        }

        let finalUrl = `/template/get-master-quick-reply-template?`;
        if (companyOutletUuid) {
          finalUrl += `&company_outlet_uuid=${companyOutletUuid}`;
        }
        finalUrl += url;

        const res = await api.get(finalUrl);
        const data = res.data.data;

        if (data.length > 0) {
          const finalData: IMasterQuickReplyTemplateData = data[0];

          const result: any = {
            components: [],
            header: {
              type: "HEADER",
              text: "",
              example: {
                header_text: [[]],
              },
            },
            body: {
              type: "BODY",
              text: "",
              example: {
                body_text: [[]],
              },
            },
            buttons: {
              type: "BUTTONS",
              buttons: [],
            },
            footer: {
              type: "FOOTER",
              text: "",
            },
          };

          for (const data of finalData.components) {
            if (data.type === "HEADER") {
              result.header.text = data.text;
              result.header.example = data.example;
            } else if (data.type === "BODY") {
              result.body.text = data.text;
            } else if (data.type === "BUTTONS") {
              result.buttons.buttons = data.buttons as any;
            } else if (data.type === "FOOTER") {
              result.footer.text = data.text;
            }
          }
          finalData.header = result.header;
          finalData.body = result.body;
          finalData.footer = result.footer;
          finalData.buttons = result.buttons;
          finalData.example = {
            body_text:
              result.body.example &&
                result.body.example.body_text &&
                result.body.example.body_text.length > 0
                ? result.body.example.body_text[0]
                : [],
            header_text:
              result.header.example &&
                result.header.example.header_text &&
                result.header.example.header_text.length > 0
                ? result.header.example.header_text[0]
                : [],
          };

          dispatch(fetchMasterQuickReplyTemplateSuccess(finalData));
        } else {
          dispatch(
            fetchMasterQuickReplyTemplateFailed(
              "Unfortunately, there are no records available at the moment."
            )
          );
        }
      } catch (err: any) {
        dispatch(fetchMasterQuickReplyTemplateFailed("Something went to be wrong!"));
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      }
    };

export const upsertMasterQuickReplyTemplate =
  (
    data: IMasterQuickReplyTemplateData,
    onCallBack: (
      isSuccess?: boolean,
      templateData?: IMasterQuickReplyTemplateData
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());

        const finalData: IMasterQuickReplyTemplatePayload = {
          master_quick_reply_template_uuid: data.master_quick_reply_template_uuid || null,
          name: data.name.toLowerCase(),
          text: data.text,
          status: data.status || "ACTIVE",
          source_table: data.source_table,
          tags: data.tags || [],
          category: data.category || "public",
          type: data.type || "quick_reply",
          attachment: data.attachment || null,
        };

        if (data.company_outlet_uuid !== undefined) {
          finalData.company_outlet_uuid = data.company_outlet_uuid;
        }

        const res = await api.post("/template/upsert-master-quick-reply-template", finalData);

        const templateData = res.data.data;
        dispatch(
          showMessage({
            type: "success",
            message:
              data.status === "INACTIVE"
                ? "Master quick reply template is deleted successfully"
                : data.master_quick_reply_template_uuid
                  ? "Master quick reply template is updated successfully"
                  : "Master quick reply template is created successfully",
            displayAs: "snackbar",
          })
        );
        onCallBack(true, templateData);
      } catch (err: any) {
        onCallBack(false, undefined);
        dispatch(
          showMessage({
            type: "error",
            message:
              JSON.stringify(err?.response?.data) ||
              "Something went to be wrong!",
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const deleteMasterQuickReplyTemplate =
  (
    company_outlet_uuid: string,
    name: string,
    onCallBack: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());

        await api.post(`/conversation/delete-quick-reply`, {
          company_outlet_uuid,
          name,
        });
        dispatch(
          showMessage({
            type: "success",
            message: "Master quick reply template is deleted successfully",
            displayAs: "snackbar",
          })
        );
        onCallBack(true);
      } catch (err: any) {
        onCallBack(false);
        dispatch(
          showMessage({
            type: "error",
            message: JSON.stringify(err.response.data),
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const CLEAR_MASTER_QUICK_REPLY_TEMPLATE_STATE =
  "CLEAR_MASTER_QUICK_REPLY_TEMPLATE_STATE";
export const CLEAR_MASTER_QUICK_REPLY_TEMPLATE = "CLEAR_MASTER_QUICK_REPLY_TEMPLATE";

export const clearMasterQuickReplyTemplateState = () =>
  action(CLEAR_MASTER_QUICK_REPLY_TEMPLATE_STATE);
export const clearMasterQuickReplyTemplate = () => action(CLEAR_MASTER_QUICK_REPLY_TEMPLATE);
